"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Sent, Cancelled" },
  { from: "Sent", to: "Accepted, Rejected, Draft, Cancelled" },
  { from: "Accepted", to: "Converted, Sent, Draft, Cancelled" },
  { from: "Rejected", to: "Draft, Cancelled" },
  {
    from: "Converted",
    to: "Cancelled only. Mark As Converted from Accepted does not create a GRPO or Bill — convert buttons then hide",
  },
  { from: "Partially Recorded", to: "Closed, Cancelled" },
  { from: "Closed", to: "Partially Recorded" },
  { from: "Cancelled", to: "Draft, Sent" },
];

const CONVERT_FROM = [
  {
    from: "Scratch",
    when: "Add with nothing pre-filled",
    result: "A new order. Save makes Draft; Save & Sent makes Sent",
  },
  {
    from: "Duplicate",
    when: "Duplicate Document on the list or the detail page",
    result:
      "Opens Add with the same vendor, items, and amounts. New PO number, today's dates, Draft, no attachments, no converted-to links",
  },
];

const CONVERT_TO = [
  {
    to: "GRPO",
    when: "Accepted or Partially Recorded. Lines with remaining quantity only",
    where:
      "Convert To GRPO on the detail, To GRPO on the row, or CONVERT → Convert to GRPO on the list",
    result:
      "Opens a new GRPO. This PO becomes Partially Recorded, or Converted when remaining quantity hits zero — after that GRPO is saved",
  },
  {
    to: "Bill",
    when: "Accepted or Partially Recorded, and no non-cancelled GRPO already exists for these POs",
    where:
      "Convert To Bills on the detail, To Bill on the row, or CONVERT → Convert to Bills on the list",
    result:
      "Opens a new Bill from remaining quantity. If a live GRPO already exists, create the bill from the GRPO instead — the API refuses PO → Bill in that case",
  },
];

const RULES = [
  {
    topic: "No ledger posting",
    rule: "A Purchase Order never posts to the general ledger. Payables and expense/inventory hit when you save a Bill (and stock add follows Inventory Config on GRPO or Bill, not on this document)",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a Purchase Order is limited by your plan. Editing, Mark As, converting, duplicating, and deleting are not",
  },
  {
    topic: "No primary-bank gate",
    rule: "Unlike Quote, Sales Order, Delivery Challan, and Invoice, you can create a PO without a primary bank",
  },
  {
    topic: "Purchase order number",
    rule: "Auto-generated with a PO prefix unless you turn that off under Purchase Order Configuration on the form. That same drawer stores Terms & Conditions. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Vendor",
    rule: "All four are required to create, plus shipping address and at least one item. After save, location and vendor stay locked",
  },
  {
    topic: "Currency",
    rule: "Taken from the vendor at creation. An exchange-rate field appears when that currency differs from the company base",
  },
  {
    topic: "No stock on this document",
    rule: "Inventory Config Stock Add Source is GRPO or Bill, never Purchase Order. Inventory Impact on detail shows Stock Add: Disabled",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Sent, Accepted, Rejected, Converted, Partially Recorded, Closed, and Cancelled cannot be edited",
  },
  {
    topic: "Editing skips the overdue-bill check",
    rule: "That check only runs when you first create a PO. Editing a Draft re-checks address and credit limit, but not overdue bills",
  },
  {
    topic: "Credit limit and overdue",
    rule: "Checked on create if Purchase Order is in this vendor's Restriction Document / Restriction Payment Terms Document (or company purchase defaults under Settings → Credit limits)",
  },
  {
    topic: "Mark As Converted is manual",
    rule: "Accepted → Converted does not create a GRPO or Bill. After that, Convert To GRPO / Convert To Bills are hidden. Real conversion from GRPO or Bill sets Partially Recorded or Converted from remaining quantity",
  },
  {
    topic: "Cancel and delete",
    rule: "Blocked if a non-cancelled GRPO or Bill was created from this PO. Cancelled can go back to Draft or Sent — this document never moved stock",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a purchase order" },
      { heading: "2. Viewing, searching, and exporting" },
      { heading: "3. Editing a purchase order" },
      { heading: "4. Printing and downloading" },
      { heading: "5. Changing status" },
      { heading: "6. Converted from" },
      { heading: "7. Converted to — GRPO or Bill" },
      { heading: "8. Duplicating a purchase order" },
      { heading: "9. Deleting a purchase order" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function PurchaseOrdersPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Purchase Orders
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone sending a formal order to a vendor before goods are
            received or billed
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Purchase Order</B> is the order you send the vendor. It
            does not post the ledger and does not add warehouse quantity.{" "}
            <B>Save</B> makes <B>Draft</B>; <B>Save &amp; Sent</B> makes{" "}
            <B>Sent</B>. After <B>Accepted</B>, remaining quantity converts
            to a <B>GRPO</B> or — if no live GRPO exists — to a{" "}
            <B>Bill</B>.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a purchase order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B>{" "}
            — the rest stays locked until those three are set. On Add you
            get <B>Save</B> (Draft) and <B>Save &amp; Sent</B>. Edit is Save
            only.
          </p>
          <Definitions
            items={[
              {
                term: "Purchase order number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Purchase Order Configuration</B> on the form (prefix
                    and next number). That same drawer stores{" "}
                    <B>Terms &amp; Conditions</B>. Default prefix is{" "}
                    <B>PO</B>. Locked after save.
                  </>
                ),
              },
              {
                term: "Vendor and shipping",
                desc: (
                  <>
                    Vendor is required and must belong to the branch.
                    Shipping is Organization or Customer (drop-ship).
                    Customer here is an address only — it does not make this
                    a sales document.
                  </>
                ),
              },
              {
                term: "Payment Terms, items, discounts, charges, tax",
                desc: (
                  <>
                    Payment terms copy from the vendor. Purchasable items
                    only. Document discount follows Settings → Discounts.
                    Extra lines follow Settings → Additional charges on this
                    module. GST from vendor state vs company state.
                  </>
                ),
              },
              {
                term: "Currency",
                desc: (
                  <>
                    From the vendor. Exchange Rate appears when it differs
                    from the company base (Settings → Currencies).
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            CreditLimitAlert can appear on save if this vendor&apos;s
            Restriction Document includes Purchase Order. Company defaults
            for that list live under Settings → Credit limits.
          </Callout>
          <Screenshot caption="Screenshot: Add Purchase Order with Save and Save & Sent, Purchase Order Configuration on the number field." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Status tabs, search, dates, division, branch, vendor, added by.
            Toolbar: <B>Add</B>; CONVERT (to GRPO / Bills);{" "}
            <B>Export Purchase Orders</B>; Preferences (same numbering /
            T&amp;Cs as the form drawer); Manage Custom Fields, Refresh
            List, Reset Columns Width.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Row: View, Duplicate, Edit (Draft), Delete, Mark As, To GRPO /
            To Bill when status allows. Detail: <B>View Vendor</B>,
            Document Flow, History, Inventory Impact.
          </p>
          <Screenshot caption="Screenshot: Purchase Order list with CONVERT, Export Purchase Orders, and status tabs." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Editing a purchase order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only. Vendor and location stay locked. Overdue-bill check
            does not re-run on edit.
          </p>
          <Screenshot caption="Screenshot: Edit disabled because the purchase order is Accepted." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail header: <B>Download</B> (PDF) and <B>Print</B>.
          </p>

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Button: <B>Mark Purchase Order As</B>. Items are{" "}
            <B>Mark As {"{status}"}</B>. There is no Open or Completed on
            this module. Remaining quantity after GRPO/Bill sets{" "}
            <B>Partially Recorded</B> or <B>Converted</B> — you do not Mark
            As those from a live conversion except the manual Accepted →
            Converted shortcut.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[200px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Mark Purchase Order As on an Accepted order." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Converted from
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_FROM.map((r, i) => (
                  <tr
                    key={r.from}
                    className={
                      i < CONVERT_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Converted to — GRPO or Bill
          </h3>
          <Callout type="warning">
            Convert to Bill from a PO is refused if a non-cancelled GRPO
            already exists for those purchase orders. Receive on GRPO first,
            then Convert To Bill on the GRPO.
          </Callout>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_TO.map((r, i) => (
                  <tr
                    key={r.to}
                    className={
                      i < CONVERT_TO.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Convert To GRPO and Convert To Bills on an Accepted purchase order." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Duplicating a purchase order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            <B>Duplicate Document</B> on the list or detail. New number,
            today&apos;s dates, Draft, no attachments, no converted-to
            links.
          </p>

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deleting a purchase order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Blocked if any GRPO or Bill was created from this PO, including
            cancelled downstream documents depending on the API check —
            treat any linked GRPO or Bill as a block. Cancel is the same
            family of check for non-cancelled downstream docs.
          </p>
          <Screenshot caption="Screenshot: delete blocked because a GRPO was created from this purchase order." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/vendors"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Vendors <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Credit limit and overdue rules that can block this PO
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/grpo"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              GRPO <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where remaining quantity is received, and stock may be added
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Direct from PO only when no live GRPO exists
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
